import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  FC,
  ReactNode,
  useEffect,
} from "react";
import EnquiryModal from "../components/EnquiryModal";
import { useOnboarding } from "./OnboardingContext";

let mounted = false;
interface FrappeTitleResponse {
  id: number;
  published_at: string;
  created_at: string;
  updated_at: string;
  frappeTitleElement: FrappeTitleElementItem[];
}

interface FrappeTitleElementItem {
  id: number;
  slug: string;
  title: string;
}

// ---------- Types ----------
interface IEnquireModalContext {
  openPopup: (url: string) => void;
}

interface B2cPageConfig {
  pageLabel?: string;
  pagePath: string;
  enableNearestOffice?: boolean;
  enableCoaching?: boolean;
}

interface B2cWizardConfig {
  enabledPages?: B2cPageConfig[];
}

interface IEnquireModalWrapperProps {
  children: ReactNode;
  b2cWizardConfig?: B2cWizardConfig;
}

// ---------- Context ----------
const EnquireModalContext = createContext<IEnquireModalContext | undefined>(
  undefined
);

// ---------- Hook ----------
export const useEnquireModal = (): IEnquireModalContext => {
  const context = useContext(EnquireModalContext);
  if (!context) {
    throw new Error("useEnquireModal must be used within EnquireModalWrapper");
  }
  return context;
};

// ---------- Provider Component ----------
const EnquireModalWrapper: FC<IEnquireModalWrapperProps> = ({ children, b2cWizardConfig }) => {
  const { openWizard } = useOnboarding();
  const [isOpen, setIsOpen] = useState(false);
  const [currentUrl, setCurrentUrl] = useState("");
  const [urlSlug, setUrlSlug] = useState("");
  const [formTitles, setFormTitles] = useState<FrappeTitleElementItem[]>([]);
  const closePopup = useCallback(() => {
    setCurrentUrl("");
    setIsOpen(false);
  }, []);

  

  const openPopup = useCallback((url: string) => {
    setUrlSlug(url);
    setCurrentUrl(process.env.NEXT_PUBLIC_CRM_CF_URL + "/" + url);
    setIsOpen(true);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest("[class]");
      if (!target) return;

      const match = Array.from(target.classList).find((cls) =>
        cls.startsWith("kc-enquire-form-") || cls === "kc-enquire-form"
      );
      if (match) {
        // Check if current page is B2C-enabled via CMS config
        const currentPath = window.location.pathname;
        const enabledPages = Array.isArray(b2cWizardConfig?.enabledPages) ? b2cWizardConfig!.enabledPages : [];
        const matchedPage = enabledPages.find((page) => currentPath === page.pagePath || currentPath.startsWith(page.pagePath));

        if (matchedPage) {
          openWizard({
            enableB2cNearestOffice: matchedPage.enableNearestOffice ?? true,
            enableB2cCoaching: matchedPage.enableCoaching ?? true,
            mode: "modal",
          });
          return;
        }

        // Default: open Frappe/JotForm
        const formId = match.slice("kc-enquire-form-".length);
        if (formId) {
          openPopup(formId);
        }
      }
    };

    document.addEventListener("click", handler);
    return () => {
      document.removeEventListener("click", handler);
    };
  }, [setIsOpen, openWizard, b2cWizardConfig]);

  const fetchFormTitles = async () => {
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_BASE_URL}/frappe-titles`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch form titles");
      }
      const data: FrappeTitleResponse = await response.json();
      mounted = true;
      setFormTitles(data?.frappeTitleElement || []);
    } catch (error) {
      console.error("Error fetching form titles:", error);
    }
  };

  useEffect(() => {
    if (mounted) {
      return;
    }
    fetchFormTitles();
  }, []);

  const contextValue = useMemo(() => ({ openPopup }), [openPopup]);

  return (
    <EnquireModalContext.Provider value={contextValue}>
      {children}
      {isOpen && (
        <EnquiryModal
          title={formTitles.find((item) => item.slug === urlSlug)?.title || formTitles.find((item) => item.slug === "default")?.title || "Enquiry Form"}
          show={isOpen}
          onClose={closePopup}
          currentPageUrl={currentUrl}
        />
      )}
    </EnquireModalContext.Provider>
  );
};

export default EnquireModalWrapper;
